// The product shell: skip link, the header line with product, tenant and user, the ruled nav row
// that shows only what the role may use, main, and the foot.
import { useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router';
import { sv } from '@aldorock-fordon/shared/sv';
import type { Me } from '../api.ts';
import { Wordmark } from './Wordmark.tsx';

type Item = { to: string; label: string; show: boolean };

function items(me: Me): Item[] {
  const p = me.permissions;
  return [
    { to: '/', label: sv.nav.start, show: true },
    { to: '/boka', label: sv.nav.book, show: p['booking.own'] },
    { to: '/kalender', label: sv.nav.calendar, show: p['calendar.view'] },
    { to: '/fordon', label: sv.nav.vehicles, show: p['vehicle.read'] },
    {
      to: '/bilpooler',
      label: sv.nav.pools,
      show: p['pool.manage'] || p['vehicle.read'] || p['booking.own'],
    },
    { to: '/nyckelskap', label: sv.nav.keys, show: p['key.manage'] },
    { to: '/rapporter', label: sv.nav.reports, show: p['report.view'] },
    { to: '/meddelanden', label: sv.nav.messages, show: true },
    { to: '/tavla', label: sv.nav.board, show: p['pool.manage'] || p['key.manage'] },
    {
      to: '/admin',
      label: sv.nav.admin,
      show: p['admin.view'] || p['import.run'] || p['billing.run'],
    },
  ].filter((i) => i.show);
}

function TenantSwitch({ me, id }: { me: Me; id: string }) {
  if (me.tenants.length < 2) return null;
  return (
    <form method="post" action="/auth/tenant" className="tenant-switch">
      <label className="visually-hidden" htmlFor={id}>
        {sv.koncern.switchLabel}
      </label>
      <div className="select">
        <select id={id} name="tenantId" defaultValue={me.tenant.id}>
          {me.tenants.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </div>
      <button className="btn" type="submit">
        {sv.koncern.switchButton}
      </button>
    </form>
  );
}

function LogoutForm({ className }: { className?: string }) {
  return (
    <form method="post" action="/auth/logout" className={className}>
      <button className="btn" type="submit">
        {sv.nav.logout}
      </button>
    </form>
  );
}

export function Shell({ me, children }: { me: Me; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const [lastPath, setLastPath] = useState(location.pathname);
  if (lastPath !== location.pathname) {
    setLastPath(location.pathname);
    setOpen(false);
  }
  const role = me.primaryRole ? me.roleLabels[me.primaryRole] : '';
  const nav = items(me);
  return (
    <div className="app-shell">
      <a className="skip-link" href="#main">
        {sv.app.skipLink}
      </a>
      <header className="band">
        <div className="band-inner">
          <Wordmark />
          <span className="band-sep" aria-hidden="true" />
          <span className="band-title">{sv.app.product}</span>
          <span className="band-facts">
            <b>{me.tenant.name}</b> · {me.user.displayName}
            {role ? <span className="role">, {role}</span> : null}
          </span>
          <div className="band-switch">
            <TenantSwitch me={me} id="tenant-switch" />
          </div>
          <LogoutForm className="logout" />
          <button
            className="btn menu-btn"
            type="button"
            aria-expanded={open}
            aria-controls="mobile-menu"
            onClick={() => setOpen((o) => !o)}
          >
            {open ? sv.app.closeMenu : sv.app.menu}
          </button>
        </div>
      </header>
      <nav className="subnav" aria-label={sv.app.mainNav}>
        <div className="nav">
          {nav.map((i) => (
            <NavLink key={i.to} to={i.to} end={i.to === '/'}>
              {i.label}
            </NavLink>
          ))}
        </div>
      </nav>
      {open ? (
        <div className="mobile-menu" id="mobile-menu">
          <nav aria-label={sv.app.mainNav}>
            <div className="menu">
              {nav.map((i) => (
                <NavLink key={i.to} to={i.to} end={i.to === '/'}>
                  {i.label}
                </NavLink>
              ))}
            </div>
          </nav>
          <p className="small muted">
            {me.tenant.name} · {me.user.displayName}
            {role ? `, ${role}` : ''}
          </p>
          <TenantSwitch me={me} id="tenant-switch-mobile" />
          <LogoutForm />
        </div>
      ) : null}
      <main id="main" className="app-main app-body" tabIndex={-1}>
        {children}
      </main>
      <footer className="app-foot">
        <p>{sv.app.supplier}</p>
      </footer>
    </div>
  );
}
